import { Flex, useMantineTheme } from '@mantine/core';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { OptionProps } from 'react-select-modern';
import { components } from 'react-select-modern';

import { EGoalType, EGoalTypeV1, type Goal as GoalModel } from '@feathr/blackbox';
import { Text, UniversalSelect } from '@feathr/components';

import type { IGoalTypeOption } from '../../Goal.utils';
import {
  getAutoGoalName,
  getGoalTypeChangeAttributes,
  getGoalTypeOptions,
  isAutoGoalName,
} from '../../Goal.utils';

interface IProps {
  isAdvancedConversionTracking: boolean;
  goal: GoalModel;
}

function GoalTypeOption(props: OptionProps<IGoalTypeOption>): JSX.Element {
  const { data } = props;
  const {
    spacing: { '1': gap },
  } = useMantineTheme();
  return (
    <components.Option {...props}>
      <Flex direction={'column'} gap={gap}>
        <Text.DefaultHeadingMedium>{data.name}</Text.DefaultHeadingMedium>
        <Text.DefaultRegular>{data.description}</Text.DefaultRegular>
      </Flex>
    </components.Option>
  );
}

function GoalTypeSelect({ isAdvancedConversionTracking, goal }: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const options = getGoalTypeOptions(t);

  function handleChangeGoalType({ id }: IGoalTypeOption): void {
    const currentName = goal.get('name') as string | undefined;
    const nameUpdate = isAutoGoalName(currentName, t) ? { name: getAutoGoalName(id, t) } : {};
    goal.set({ ...getGoalTypeChangeAttributes(id), ...nameUpdate });
  }

  const type = goal.get('conv_type');
  const isV1 = type === EGoalTypeV1.custom;
  // V1 goals need to be cast as group_entry or conversion_pixel until they are migrated to EGoalType.group_entry or EGoalType.conversion_pixel
  const goalType = (function () {
    if (isV1) {
      if (isAdvancedConversionTracking) {
        return EGoalType.conversion_pixel;
      }
      return EGoalType.group_entry;
    }
    return type;
  })();
  const value = options.find((option) => option.id === goalType);

  return (
    <UniversalSelect<IGoalTypeOption>
      components={{ Option: GoalTypeOption }}
      label={t('Goal type')}
      onSelectSingle={handleChangeGoalType}
      options={options}
      value={value}
    />
  );
}

export default observer(GoalTypeSelect);
